<script setup lang="ts">
import { computed, ref } from "vue";
import { useClipboard } from "@vueuse/core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Copy, Check, Sparkles, Lock } from "lucide-vue-next";
import CodeBlock from "@/components/CodeBlock.vue";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";

interface Props {
    token: string;
    eventType?: { isTeamEventType?: boolean };
    embedType?: "group";
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (e: "back"): void;
}>();

const { copy, copied, isSupported } = useClipboard();
const { hasSubscription } = useHasSubscription(props.eventType);
const proDialog = useProDialog();

const selector = ref(".your-booking-link");

function showProModal() {
    proDialog.show({
        feature: "embed_customization",
    });
}

const code = computed(() => {
    const options = {
        type: props.embedType === "group" ? "group" : "single",
        config: { token: props.token },
        selector: selector.value,
    };

    const optionsStr = JSON.stringify(options, null, 4);

    // Extract class name from selector for example element
    const exampleClass = selector.value.startsWith(".")
        ? selector.value.slice(1)
        : selector.value.startsWith("#")
          ? ""
          : selector.value;

    const exampleAttr = selector.value.startsWith("#")
        ? `id="${selector.value.slice(1)}"`
        : `class="${exampleClass}"`;

    return `<!-- Your custom styled element -->
<a href="#" ${exampleAttr}>Schedule a call</a>

<${"script"} src="https://iframe.zaptime.app/zaptime.js"></${"script"}>
<${"script"}>
    Zaptime.popup(${optionsStr});
</${"script"}>`;
});

function copyCode() {
    copy(code.value);
}
</script>

<template>
    <div class="space-y-4">
        <button
            @click="emit('back')"
            class="gap-1 mb-2 text-sm text-iron-600 hover:text-iron-900 flex items-center"
        >
            <ArrowLeft :size="16" />
            {{ $t("event_types.share_hub.embed.back") }}
        </button>

        <div>
            <h3 class="mb-1 font-medium text-iron-900 gap-2 flex items-center">
                {{ $t("event_types.share_hub.embed.element.title") }}
                <span
                    v-if="!hasSubscription"
                    class="gap-1 from-orange-400 to-pink-500 px-2 py-0.5 text-xs font-medium text-white inline-flex items-center rounded-full bg-gradient-to-r"
                >
                    PRO
                </span>
            </h3>
            <p class="text-sm text-iron-500">
                {{ $t("event_types.share_hub.embed.element.full_description") }}
            </p>
        </div>

        <!-- PRO-only content -->
        <template v-if="hasSubscription">
            <div
                class="space-y-4 rounded-lg border-iron-200 bg-iron-50 p-4 border"
            >
                <div
                    class="text-xs font-medium tracking-wide text-iron-500 uppercase"
                >
                    {{ $t("event_types.share_hub.embed.popup.options_title") }}
                </div>

                <div class="space-y-1">
                    <Label>
                        {{
                            $t(
                                "event_types.share_hub.embed.element.selector_label",
                            )
                        }}
                    </Label>
                    <Input
                        v-model="selector"
                        placeholder=".your-booking-link"
                    />
                    <p class="text-xs text-iron-500">
                        {{
                            $t(
                                "event_types.share_hub.embed.element.selector_description",
                            )
                        }}
                    </p>
                </div>
            </div>

            <div class="rounded-lg bg-iron-50 p-4">
                <CodeBlock :code="code" />
            </div>

            <Button
                v-if="isSupported"
                variant="outline"
                @click="copyCode"
                class="gap-2"
            >
                <Check v-if="copied" :size="16" />
                <Copy v-else :size="16" />
                {{
                    copied
                        ? $t("copied")
                        : $t("event_types.share_hub.embed.copy_code")
                }}
            </Button>
        </template>

        <!-- PRO teaser for non-subscribed users -->
        <div v-else class="rounded-lg border-iron-200 bg-iron-50 p-6 border">
            <div class="flex flex-col items-center text-center">
                <div
                    class="mb-4 h-12 w-12 from-orange-400 to-pink-500 flex items-center justify-center rounded-full bg-gradient-to-br"
                >
                    <Sparkles :size="20" class="text-white" />
                </div>
                <h4 class="mb-2 font-medium text-iron-900">
                    {{
                        $t(
                            "event_types.share_hub.embed.element.pro_teaser_title",
                        )
                    }}
                </h4>
                <p class="mb-4 text-sm text-iron-500">
                    {{
                        $t(
                            "event_types.share_hub.embed.element.pro_teaser_description",
                        )
                    }}
                </p>
                <Button @click="showProModal" class="gap-2">
                    <Lock :size="14" />
                    {{
                        $t("event_types.share_hub.embed.element.unlock_button")
                    }}
                </Button>
            </div>
        </div>
    </div>
</template>
